/**
 * Vorlage „Ausrollen“.
 *
 * Draufsicht auf das bemehlte Brett. Das Nudelholz rollt den Teig erst von
 * der Mitte nach oben und unten, dann – um 90 Grad gedreht – nach links und
 * rechts aus. Der Teig wird dabei größer und flacher.
 */
import { ease, lerp, segment } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import { ellipsePath } from '../paths.ts';
import { circle, compact, faded, group, shape, styles } from '../scene.ts';
import type { SceneNode } from '../scene.ts';
import { board } from '../parts/knife.ts';
import { badge, doubleArrow } from '../parts/stage.ts';
import { rollingPin } from '../parts/utensils.ts';
import { defineTemplate, numberValue } from '../template.ts';

/** Mitte des Teigs. */
const CENTRE = { x: 120, y: 74 } as const;

/** Zeitplan. */
const TIME = {
  vertical: [0.04, 0.44],
  turn: [0.44, 0.52],
  horizontal: [0.52, 0.86],
  turnBack: [0.86, 0.94],
  reset: [0.9, 1],
} as const;

/** Mehlstaub auf dem Brett. */
const FLOUR: readonly (readonly [number, number])[] = [
  [40, 30],
  [58, 120],
  [190, 34],
  [204, 112],
  [30, 92],
  [214, 70],
  [70, 24],
  [168, 124],
];

/** Hin und Her innerhalb eines Abschnitts: beginnt und endet in der Mitte. */
function stroke(t: number, range: readonly [number, number], cycles: number): number {
  const local = segment(t, range[0], range[1]);
  return Math.sin(local * cycles * Math.PI * 2);
}

/** Vorlage „Ausrollen“. */
export const bakeRollTemplate = defineTemplate({
  id: 'bake.roll',
  group: 'bake',
  title: 'Ausrollen',
  description:
    'Der Teig wird auf der bemehlten Fläche von der Mitte aus mit dem Nudelholz ausgerollt, zwischendurch gedreht, bis er gleichmäßig dünn ist.',
  durationSeconds: 7,
  keywords: ['ausrollen', 'auswellen', 'dünn ausrollen', 'nudelholz', 'teigrolle', 'rollen'],
  stillTime: 0.3,
  parameters: [
    {
      kind: 'number',
      name: 'thickness',
      label: 'Dicke',
      min: 1,
      max: 20,
      step: 1,
      unit: 'mm',
      defaultValue: 4,
    },
  ],
  inferValues(text) {
    const lower = text.toLowerCase();
    const mm = /(\d+(?:[.,]\d+)?)\s*mm/.exec(lower);
    if (mm?.[1] !== undefined) return { thickness: Math.round(Number(mm[1].replace(',', '.'))) };
    const cm = /(\d+(?:[.,]\d+)?)\s*cm\s*dick/.exec(lower);
    if (cm?.[1] !== undefined) return { thickness: Math.round(Number(cm[1].replace(',', '.')) * 10) };
    return {};
  },
  scene(values, t) {
    const thickness = numberValue(values, 'thickness', 4);
    const reset = ease.inOutSine(segment(t, ...TIME.reset));
    const growY = ease.inOutSine(segment(t, ...TIME.vertical));
    const growX = ease.inOutSine(segment(t, ...TIME.horizontal));
    const dough = (gx: number, gy: number): SceneNode => {
      const rx = lerp(34, 82, gx);
      const ry = lerp(24, 50, gy);
      return shape(ellipsePath(CENTRE.x, CENTRE.y, rx, ry), styles.snow(STROKE.outline));
    };
    const ry = lerp(24, 50, growY);
    const rx = lerp(34, 82, growX);

    // Lage des Nudelholzes: waagerecht über y, nach der Drehung senkrecht über x.
    const turn = ease.inOutCubic(segment(t, ...TIME.turn)) - ease.inOutCubic(segment(t, ...TIME.turnBack));
    const dy = stroke(t, TIME.vertical, 2) * ry * 0.62;
    const dx = stroke(t, TIME.horizontal, 2) * rx * 0.62;
    const lift =
      Math.sin(segment(t, ...TIME.turn) * Math.PI) + Math.sin(segment(t, ...TIME.turnBack) * Math.PI);
    // Gerundet, damit winzige Rechenreste die Markierungen der Rolle nicht springen lassen.
    const roll = Math.round(((dy + dx) / 60) * 1e6) / 1e6;
    const pinScale = 0.9 * (1 + 0.06 * lift);
    const pin = group([group(rollingPin(roll), { transform: { scale: pinScale, ox: 0, oy: 0 } })], {
      transform: { x: CENTRE.x + dx, y: CENTRE.y + dy, rotate: 90 * turn, ox: 0, oy: 0 },
    });
    const pinShadow = group([shape(ellipsePath(0, 0, 58 * pinScale, 9), styles.flat('shade'))], {
      transform: {
        x: CENTRE.x + dx + 4 + lift * 6,
        y: CENTRE.y + dy + 6 + lift * 6,
        rotate: 90 * turn,
        ox: 0,
        oy: 0,
      },
    });
    return compact([
      ...board(),
      ...FLOUR.map(([x, y]) => circle(x, y, 1.5, styles.flat('shade'))),
      reset > 0 ? group([dough(0, 0)]) : null,
      faded(1 - reset, [dough(growX, growY)]),
      pinShadow,
      pin,
      badge(196, 122, `${thickness} mm`),
    ]);
  },
  hints() {
    return [
      doubleArrow({ x: CENTRE.x - 70, y: CENTRE.y }, { x: CENTRE.x + 70, y: CENTRE.y }),
      doubleArrow({ x: CENTRE.x, y: CENTRE.y - 46 }, { x: CENTRE.x, y: CENTRE.y + 46 }),
    ];
  },
});
